// src/pages/Locations.js
// Lists upcoming market dates, pulled live from Shopify (see data/locations.js)
// so City/Venue/Date/Confirmed can be managed in Shopify admin with no deploy.

import React, { useState, useEffect } from 'react';
import { MapPin, Calendar } from 'lucide-react';
import { getMarketDates, upcoming, formatDateRange } from '../data/locations';
import usePageTitle from '../hooks/usePageTitle';

// Temporary: Marina Market (Cork) promo banner shown above the dates list.
// Hides itself once the market is over (after Sun 25 Oct 2026).
const MARKET_BANNER_UNTIL = new Date('2026-10-26T00:00:00');

const Locations = () => {
    usePageTitle('Find Us at a Market', 'Upcoming MAISON D\'OUD market and pop-up dates.');

    const [dates, setDates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMarketDates()
            .then(all => setDates(upcoming(all)))
            .catch(() => setDates([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="bg-background min-h-screen pt-32 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground tracking-wide">Loading...</div>
        </div>
    );

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-4 sm:px-6 transition-colors duration-300">
            <div className="max-w-3xl mx-auto">

                <div className="text-center mb-12 sm:mb-16">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-foreground mb-4 tracking-wide">
                        Find Us at a Market
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg">
                        Where to find MAISON D'OUD in person.
                    </p>
                </div>

                {new Date() < MARKET_BANNER_UNTIL && (
                    <img
                        src="/images/marina-market/marina-market-banner-two-new-scents.jpg"
                        alt="MAISON D'OUD: two new scents at Marina Market, Cork, Sat 24 & Sun 25 Oct. Limited stock, testing available."
                        width="1600"
                        height="838"
                        decoding="async"
                        className="w-full h-auto rounded-2xl border border-border mb-8 sm:mb-12"
                    />
                )}

                {dates.length === 0 && (
                    <div className="text-center py-16">
                        <div className="w-16 h-16 bg-[#CEAC8F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Calendar className="w-8 h-8 text-[#CEAC8F]/40" />
                        </div>
                        <p className="text-muted-foreground text-lg mb-2">No upcoming markets scheduled.</p>
                        <p className="text-muted-foreground/60 text-sm">Check back soon — new dates are added regularly.</p>
                    </div>
                )}

                {dates.length > 0 && (
                    <div className="space-y-4">
                        {dates.map((d) => (
                            <div
                                key={d.id}
                                className="flex items-center justify-between gap-4 bg-card rounded-2xl p-6 border border-border"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-[#CEAC8F]/10 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-5 h-5 text-[#CEAC8F]" />
                                    </div>
                                    <div>
                                        <h3 className="text-foreground font-medium tracking-wide">{d.city}</h3>
                                        {d.venue && (
                                            <p className="text-muted-foreground text-sm">{d.venue}</p>
                                        )}
                                        <p className="text-muted-foreground/70 text-sm mt-1">
                                            {formatDateRange(d.startDate, d.endDate)}
                                        </p>
                                    </div>
                                </div>

                                {/* Live status indicator — reflects whatever Confirmed is
                                    set to in Shopify admin right now, no deploy needed. */}
                                <span
                                    className={`flex-shrink-0 inline-flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest ${
                                        d.confirmed
                                            ? 'bg-green-600/10 text-green-600'
                                            : 'bg-muted text-muted-foreground'
                                    }`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${d.confirmed ? 'bg-green-600 animate-pulse' : 'bg-muted-foreground/50'}`} />
                                    {d.confirmed ? 'Confirmed' : 'Date TBC'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Locations;
